import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, ShieldCheck } from "lucide-react";
import api, { formatApiError, setStoredClient } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

export default function Landing() {
  const navigate = useNavigate();
  const { user, loading: authLoading, clearSession } = useAuth();
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.get("/public/clients/resolve", { params: { code: code.trim() } });
      clearSession();
      setStoredClient(data);
      navigate("/login");
    } catch (err) {
      setError(err?.response?.status === 404 ? "Codice non valido" : formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) return null;
  // Logged-in users stay in their own workspace until they log out
  if (user?.client_id) return <Navigate to={user.role === "admin" ? "/admin" : "/responsabile"} replace />;

  return (
    <div className="relative min-h-screen flex flex-col bg-slate-950 text-white overflow-hidden" data-testid="landing-page">
      <div className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-blue-600/25 blur-3xl" aria-hidden />
      <div className="absolute bottom-0 right-0 h-[420px] w-[420px] rounded-full bg-amber-500/10 blur-3xl" aria-hidden />

      <div className="relative z-10 flex-1 flex items-center px-6 sm:px-12 lg:px-24 py-16">
        <div className="w-full max-w-xl anim-fade-up">
          <img src="/brand/lmkt.png" alt="LMKT" className="h-28 w-28 sm:h-32 sm:w-32 object-contain" data-testid="landing-lmkt-logo" />
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mt-10" data-testid="landing-title">
            Dance Analysis System
          </h1>
          <p className="text-slate-400 mt-4 text-sm sm:text-base max-w-md">
            Inserisci il codice di accesso fornito dalla tua organizzazione per entrare nel sistema.
          </p>

          <form onSubmit={onSubmit} className="mt-10 flex flex-col sm:flex-row gap-3" data-testid="landing-code-form">
            <Input
              required
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="CODICE DI ACCESSO"
              className="h-14 bg-white/5 border-white/15 text-white placeholder:text-slate-500 text-lg tracking-[0.3em] font-semibold focus-visible:ring-blue-500"
              data-testid="landing-code-input"
            />
            <Button
              type="submit"
              disabled={loading}
              className="h-14 px-8 bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-md transition-colors"
              data-testid="landing-code-submit"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Accedi <ArrowRight className="h-4 w-4 ml-2" /></>}
            </Button>
          </form>
          {error && (
            <div className="mt-4 text-sm font-medium text-rose-300" data-testid="landing-code-error">{error}</div>
          )}
        </div>
      </div>

      <footer className="relative z-10 px-6 sm:px-12 lg:px-24 py-6 flex items-center justify-between text-xs text-slate-500">
        <span>Sviluppato da <span className="font-semibold text-slate-300">LMKT</span> — Simone Arrighi &amp; Valeria Tria</span>
        <Link to="/superadmin/login" className="flex items-center gap-1.5 hover:text-white transition-colors" data-testid="landing-superadmin-link">
          <ShieldCheck className="h-3.5 w-3.5" /> Area Superadmin
        </Link>
      </footer>
    </div>
  );
}
